/**
 * Pure, React-free helpers for the Template domain: field-type metadata,
 * blank-entity factories, value coercion, id/versioning helpers, and grouping.
 */

import { v4 as uuid } from 'uuid';
import type {
  FieldDef,
  FieldType,
  OutputDef,
  Template,
  TemplateGroup,
  TemplateSummary,
} from './types';

/** All field types with display metadata (used by the builder's type picker). */
export const FIELD_TYPES: { value: FieldType; label: string; hint: string }[] = [
  { value: 'number', label: 'Number', hint: 'A base numeric input' },
  { value: 'factor', label: 'Factor', hint: 'A scaling factor (e.g. 1.25)' },
  { value: 'multiplier', label: 'Multiplier', hint: 'A multiplier applied to a base' },
  { value: 'threshold', label: 'Threshold', hint: 'A cutoff/limit value' },
  { value: 'enum', label: 'Enum', hint: 'One of a fixed set of choices' },
  { value: 'text', label: 'Text', hint: 'Free-form text' },
  { value: 'boolean', label: 'Boolean', hint: 'A true/false toggle' },
];

/** Field types whose values are numeric (drive min/max + numeric coercion). */
export const NUMERIC_FIELD_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  'number',
  'factor',
  'multiplier',
  'threshold',
]);

export function isNumericFieldType(type: FieldType): boolean {
  return NUMERIC_FIELD_TYPES.has(type);
}

/** Suggests a camelCase machine key from a human label. */
export function suggestKey(label: string): string {
  const words = label
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '';
  return words
    .map((w, i) => (i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join('');
}

/** Creates a new blank field with a unique placeholder key. */
export function blankField(type: FieldType = 'number'): FieldDef {
  const suffix = uuid().slice(0, 4);
  return {
    key: `field_${suffix}`,
    label: '',
    type,
    ...(type === 'enum' ? { options: [] } : {}),
  };
}

/** Creates a new blank derived output. */
export function blankOutput(): OutputDef {
  const suffix = uuid().slice(0, 4);
  return { key: `output_${suffix}`, label: '', expression: '' };
}

/** Creates a new, empty version-1 template shell for the builder. */
export function blankTemplate(createdBy: string): Template {
  return {
    id: '',
    name: '',
    version: 1,
    description: '',
    createdBy,
    updatedAt: new Date().toISOString(),
    fields: [],
    outputs: [],
  };
}

/** Deterministic document id for a template name + version. */
export function templateDocId(name: string, version: number): string {
  return `${name}@${version}`;
}

/**
 * Coerces a raw editor string into the correct value for a field type.
 * Returns undefined when the input is empty (so no default is stored).
 */
export function coerceDefaultValue(
  type: FieldType,
  raw: string,
): string | number | boolean | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  if (isNumericFieldType(type)) {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : undefined;
  }
  if (type === 'boolean') {
    return trimmed === 'true';
  }
  return trimmed;
}

/** Renders a field's default value back to a display/editor string. */
export function defaultValueToString(value: FieldDef['defaultValue']): string {
  if (value === undefined || value === null) return '';
  return String(value);
}

/** Moves an item within an array by delta (-1 up, +1 down); returns a new array. */
export function moveItem<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return items;
  const next = items.slice();
  const [removed] = next.splice(index, 1);
  next.splice(target, 0, removed);
  return next;
}

/**
 * Validates a template for save. Returns a list of human-readable problems;
 * an empty list means the template is valid.
 */
export function validateTemplate(t: Template): string[] {
  const problems: string[] = [];
  if (!t.name.trim()) problems.push('Template name is required.');

  const seenFieldKeys = new Set<string>();
  t.fields.forEach((f, i) => {
    const n = i + 1;
    if (!f.key.trim()) problems.push(`Field ${n}: key is required.`);
    if (!f.label.trim()) problems.push(`Field ${n}: label is required.`);
    if (f.key.trim()) {
      if (seenFieldKeys.has(f.key)) {
        problems.push(`Field ${n}: duplicate key "${f.key}".`);
      }
      seenFieldKeys.add(f.key);
    }
    if (f.type === 'enum' && (!f.options || f.options.length === 0)) {
      problems.push(`Field ${n} ("${f.label || f.key}"): enum needs at least one option.`);
    }
    if (
      f.min !== undefined &&
      f.max !== undefined &&
      f.min > f.max
    ) {
      problems.push(`Field ${n} ("${f.label || f.key}"): min cannot exceed max.`);
    }
  });

  const seenOutputKeys = new Set<string>();
  (t.outputs ?? []).forEach((o, i) => {
    const n = i + 1;
    if (!o.key.trim()) problems.push(`Output ${n}: key is required.`);
    if (!o.expression.trim()) problems.push(`Output ${n}: expression is required.`);
    if (o.key.trim()) {
      if (seenOutputKeys.has(o.key)) {
        problems.push(`Output ${n}: duplicate key "${o.key}".`);
      }
      seenOutputKeys.add(o.key);
    }
  });

  return problems;
}

/** Groups version summaries by name; each group is sorted latest-version-first. */
export function groupTemplateSummaries(
  summaries: TemplateSummary[],
): TemplateGroup[] {
  const byName = new Map<string, TemplateSummary[]>();
  for (const s of summaries) {
    const list = byName.get(s.name) ?? [];
    list.push(s);
    byName.set(s.name, list);
  }
  const groups: TemplateGroup[] = [];
  for (const [name, list] of byName) {
    const versions = list.slice().sort((a, b) => b.version - a.version);
    groups.push({ name, latest: versions[0], versions });
  }
  return groups.sort((a, b) =>
    (b.latest.updatedAt ?? '').localeCompare(a.latest.updatedAt ?? ''),
  );
}
